import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { customerApi, loyaltyApi } from '../config/api';

const CustomerContext = createContext(null);

const STORAGE_KEY = 'zozo_customer';
const CONSENT_KEY = 'zozo_consent';

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeStorage(data) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}

function clearStorage() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CONSENT_KEY);
  } catch {}
}

function hasConsent() {
  try { return localStorage.getItem(CONSENT_KEY) === 'true'; } catch { return false; }
}

function setConsent(val) {
  try { localStorage.setItem(CONSENT_KEY, val ? 'true' : 'false'); } catch {}
}

export function CustomerProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);
  const [loyaltyBalance, setLoyaltyBalance] = useState(0);
  const [loyaltyAccount, setLoyaltyAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const fetchLoyaltyBalance = useCallback(async (token) => {
    try {
      const res = await loyaltyApi.getBalance(token);
      if (res.data?.success && res.data?.data) {
        setLoyaltyBalance(res.data.data.balance || 0);
        setLoyaltyAccount(res.data.data.has_account ? res.data.data : null);
      }
    } catch {}
  }, []);

  // On mount: try to recognize customer from localStorage
  useEffect(() => {
    (async () => {
      const stored = readStorage();
      if (stored?.token) {
        try {
          const res = await customerApi.recognize(stored.token);
          if (res.data?.success && res.data?.data) {
            const d = res.data.data;
            setCustomer({
              id: d.customer_id,
              name: d.name,
              email: d.email,
              phone: d.phone,
              addresses: d.addresses || [],
            });
            setLastOrder(d.last_order || null);
            // Fetch loyalty balance
            await fetchLoyaltyBalance(stored.token);
          } else {
            clearStorage();
          }
        } catch {
          // Token invalid or server error
        }
      }
      setLoading(false);
    })();
  }, [fetchLoyaltyBalance]);

  const refreshLoyalty = useCallback(async () => {
    const stored = readStorage();
    if (stored?.token) {
      await fetchLoyaltyBalance(stored.token);
    }
  }, [fetchLoyaltyBalance]);

  const saveCustomer = useCallback(async (data) => {
    try {
      const res = await customerApi.save({
        email: data.email,
        phone: data.phone,
        name: data.name,
        address: data.address,
        save_consent: true,
      });
      if (res.data?.success) {
        const token = res.data.data.recognition_token;
        const customerId = res.data.data.customer_id;
        writeStorage({ token, customerId });
        setConsent(true);
        setCustomer(prev => ({
          ...prev,
          id: customerId,
          name: data.name,
          email: data.email,
          phone: data.phone,
        }));
        // Refresh loyalty balance
        await fetchLoyaltyBalance(token);
        return token;
      }
    } catch {}
    return null;
  }, [fetchLoyaltyBalance]);

  const loginWithMagicLink = useCallback(async (token) => {
    try {
      const res = await customerApi.verifyMagicLink(token);
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        writeStorage({ token: d.recognition_token, customerId: d.customer_id });
        setConsent(true);
        setCustomer({
          id: d.customer_id,
          name: d.name,
          email: d.email,
          phone: d.phone,
          addresses: d.addresses || [],
        });
        // Refresh loyalty
        await fetchLoyaltyBalance(d.recognition_token);
        return { success: true, orders: d.orders || [] };
      }
      return { success: false, error: res.data?.error || 'unknown' };
    } catch {
      return { success: false, error: 'network' };
    }
  }, [fetchLoyaltyBalance]);

  const logout = useCallback(() => {
    clearStorage();
    setCustomer(null);
    setLastOrder(null);
    setLoyaltyBalance(0);
    setLoyaltyAccount(null);
    setDismissed(false);
  }, []);

  const dismiss = useCallback(() => setDismissed(true), []);

  const value = {
    customer,
    lastOrder,
    loyaltyBalance,
    loyaltyAccount,
    loading,
    dismissed,
    hasConsent: hasConsent(),
    saveCustomer,
    loginWithMagicLink,
    logout,
    dismiss,
    setCustomer,
    setLastOrder,
    refreshLoyalty,
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error('useCustomer must be used within CustomerProvider');
  return ctx;
}
